import React, { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar, 
  Avatar, 
  Typography, 
  IconButton,
  Badge,
  Divider,
  Paper,
  Box,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  Stack
} from '@mui/material';
import { 
  Tablet as TabletIcon,
  SignalCellularAlt as SignalIcon,
  SignalCellularOff as NoSignalIcon,
  Cancel as CancelIcon,
  Battery5Bar as BatteryIcon,
  BatteryChargingFull as BatteryChargingIcon,
  Wifi as WifiIcon,
  DataUsage as DataIcon,
  Sync as SyncIcon,
  DragIndicator as DragIndicatorIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { Device } from '../types';
import { formatLastUpdate } from '../utils/format';
import { useDevices } from '../hooks/useDevices';
import { useAuth } from '../contexts/AuthContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface DeviceListProps {
  devices: Device[];
  onDeviceSelect: (device: Device) => void;
  onRequestUpdate: (deviceId: string) => Promise<void>;
}

interface SortableDeviceItemProps {
  device: Device;
  onDeviceSelect: (device: Device) => void;
  hasPendingUpdate: boolean;
  failedUpdate?: { errorMessage: string; updatedAt: number };
  onCancelUpdate: (deviceId: string) => void;
  isFirst: boolean;
  isCanceling: boolean;
  onSync: (deviceId: string) => void;
}

const SortableDeviceItem: React.FC<SortableDeviceItemProps> = ({
  device,
  onDeviceSelect,
  hasPendingUpdate,
  failedUpdate,
  onCancelUpdate,
  isFirst,
  isCanceling,
  onSync
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: device.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {!isFirst && <Divider variant="inset" component="li" />}
      <ListItem
        alignItems="flex-start"
        onClick={() => onDeviceSelect(device)}
        sx={{ 
          cursor: 'pointer',
          py: 1.5,
          pr: device.statusBar ? 18 : 2,
          minHeight: 72,
          bgcolor: isDragging ? 'action.hover' : 'background.paper',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        {/* Handle de Arrasto */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.disabled',
            mr: 1,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon />
        </Box>

        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: device.online ? '#4285F4' : '#9E9E9E',
              color: 'white',
            }}
          >
            <TabletIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body1" fontWeight="500">
                {device.customName || device.name}
              </Typography>
              {device.isActive === false && (
                <Chip 
                  label="Desativado" 
                  size="small" 
                  color="error" 
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {/* Ícone de Sincronização */}
              <Tooltip title="Sincronizar dispositivo">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSync(device.id);
                  }}
                  sx={{
                    ml: 'auto',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(66, 133, 244, 0.08)',
                    },
                  }}
                >
                  <SyncIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
          secondary={
            <React.Fragment>
            <Stack spacing={0.5} sx={{ pr: device.statusBar ? 16 : 0, mt: 0.5 }}>
              {/* Linha 1: Empresa e Código na mesma linha */}
              {(device.companyName || device.code) && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {device.companyName && (
                    <Chip
                      label={device.companyName}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ 
                        height: 18,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  )}
                  {device.code && (
                    <Chip
                      label={device.code}
                      size="small"
                      sx={{ 
                        height: 18,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        bgcolor: 'grey.800',
                        color: 'white',
                        '& .MuiChip-label': { px: 0.8 }
                      }}
                    />
                  )}
                </Box>
              )}
              
              {/* Linha 2: Versões dos Apps */}
              {device.installedApps && device.installedApps.length > 0 && (() => {
                const launcher = device.installedApps.find(app => app.packageName === 'br.com.datazem.minimallauncher');
                const totem = device.installedApps.find(app => app.packageName === 'indecx.app.totem');
                
                return (launcher || totem) && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {launcher && (
                      <Chip
                        label={`Launcher ${launcher.versionName}`}
                        size="small"
                        sx={{ 
                          height: 18,
                          fontSize: '0.625rem',
                          bgcolor: '#e8f5e9',
                          color: '#2e7d32',
                          fontWeight: 500,
                          '& .MuiChip-label': { px: 0.8 }
                        }}
                      />
                    )}
                    {totem && (
                      <Chip
                        label={`Totem ${totem.versionName}`}
                        size="small"
                        sx={{ 
                          height: 18,
                          fontSize: '0.625rem',
                          bgcolor: '#e3f2fd',
                          color: '#1565c0',
                          fontWeight: 500,
                          '& .MuiChip-label': { px: 0.8 }
                        }}
                      />
                    )}
                  </Box>
                );
              })()}
              
              {/* Linha 3: Data e Nome do dispositivo inline */}
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.65rem',
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                {formatLastUpdate(device.lastUpdate)}
                {device.customName && (
                  <Typography 
                    component="span"
                    variant="caption"
                    sx={{ 
                      fontSize: '0.625rem',
                      color: 'text.disabled',
                      fontStyle: 'italic',
                      ml: 0.5
                    }}
                  >
                    • {device.name}
                  </Typography>
                )}
              </Typography>
              
              {failedUpdate && (
                <Tooltip title={failedUpdate.errorMessage} arrow>
                  <Chip
                    icon={<ErrorIcon />}
                    label="Última atualização falhou"
                    color="error"
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.5, maxWidth: '100%' }}
                  />
                </Tooltip>
              )}

              {/* Atualização pendente mais sutil */}
              {hasPendingUpdate && (
                <Box 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 0.5,
                    maxWidth: '100%'
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'warning.main',
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    ⚠️ Pendente
                  </Typography>
                  <Tooltip title="Cancelar atualização">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancelUpdate(device.id);
                      }}
                      disabled={isCanceling}
                      sx={{
                        color: 'error.main',
                        padding: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        },
                      }}
                    >
                      <CancelIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Stack>

              {device.statusBar && (
                <Box sx={{ 
                  position: 'absolute', 
                  right: 16, 
                  top: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 0.3
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }}>
                      Bateria: {device.statusBar.battery.level}%
                    </Typography>
                    {device.statusBar.battery.charging ? (
                      <BatteryChargingIcon sx={{ fontSize: 12, color: 'success.main' }} />
                    ) : (
                      <BatteryIcon sx={{ fontSize: 12, color: device.statusBar.battery.level > 20 ? 'primary.main' : 'error.main' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }}>
                      WIFI: {device.statusBar.wifi.connected ? 'Conectado' : 'Desconectado'}
                    </Typography>
                    <WifiIcon sx={{ 
                      fontSize: 12, 
                      color: device.statusBar.wifi.connected ? 'primary.main' : 'text.disabled' 
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }}>
                      Dados: {device.statusBar.network.connected ? 'Conectado' : 'Desconectado'}
                    </Typography>
                    <DataIcon sx={{ 
                      fontSize: 12, 
                      color: device.statusBar.network.connected ? 'primary.main' : 'text.disabled' 
                    }} />
                  </Box>
                </Box>
              )}
            </React.Fragment>
          }
        />
      </ListItem>
    </Box>
  );
};

const DeviceList: React.FC<DeviceListProps> = ({ devices, onDeviceSelect, onRequestUpdate }) => {
  const { user } = useAuth();
  const { pendingUpdates, failedUpdates, cancelUpdate } = useDevices(!!user);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [sortedDevices, setSortedDevices] = useState<Device[]>(devices);
  const [cancelStatus, setCancelStatus] = useState<{
    show: boolean;
    success: boolean;
    message: string;
  }>({ show: false, success: false, message: '' });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Chave do localStorage por usuário
  const getStorageKey = () => `device-order-${user?.uid || 'default'}`;

  // Salvar ordem no localStorage
  const saveDeviceOrder = (orderedDevices: Device[]) => {
    try {
      const deviceIds = orderedDevices.map(d => d.id);
      localStorage.setItem(getStorageKey(), JSON.stringify(deviceIds));
    } catch (error) {
      console.error('Erro ao salvar ordem dos dispositivos:', error);
    }
  };

  // Carregar ordem do localStorage
  const loadDeviceOrder = (devicesToSort: Device[]): Device[] => {
    try {
      const savedOrder = localStorage.getItem(getStorageKey());
      if (!savedOrder) return devicesToSort;

      const deviceIds: string[] = JSON.parse(savedOrder);
      
      // Criar um mapa para acesso rápido aos dispositivos
      const deviceMap = new Map(devicesToSort.map(d => [d.id, d]));
      
      // Ordenar de acordo com a ordem salva
      const ordered: Device[] = [];
      const unordered: Device[] = [];
      
      // Primeiro, adicionar dispositivos na ordem salva
      deviceIds.forEach(id => {
        const device = deviceMap.get(id);
        if (device) {
          ordered.push(device);
          deviceMap.delete(id);
        }
      });
      
      // Adicionar dispositivos novos que não estavam na ordem salva
      deviceMap.forEach(device => unordered.push(device));
      
      return [...ordered, ...unordered];
    } catch (error) {
      console.error('Erro ao carregar ordem dos dispositivos:', error);
      return devicesToSort;
    }
  };

  // Aplicar ordem salva quando os dispositivos mudarem
  React.useEffect(() => {
    const orderedDevices = loadDeviceOrder(devices);
    setSortedDevices(orderedDevices);
  }, [devices, user?.uid]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSortedDevices((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Salvar a nova ordem no localStorage
        saveDeviceOrder(newOrder);
        
        return newOrder;
      });
    }
  };

  const handleCancelUpdate = async (deviceId: string) => {
    if (!deviceId) return;
    
    setSelectedDeviceId(deviceId);
    
    try {
      const success = await cancelUpdate(deviceId);
      
      setCancelStatus({
        show: true,
        success,
        message: success 
          ? 'Atualização cancelada com sucesso' 
          : 'Não foi possível encontrar atualizações pendentes'
      });
    } catch (err) {
      setCancelStatus({
        show: true,
        success: false,
        message: 'Erro ao cancelar atualização'
      });
    } finally {
      setSelectedDeviceId(null);
    }
  };

  const handleSync = (deviceId: string) => {
    // Placeholder - implementação futura
    console.log('Sincronizar dispositivo:', deviceId);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedDevices.map(d => d.id)}
          strategy={verticalListSortingStrategy}
        >
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {sortedDevices.map((device, index) => (
              <SortableDeviceItem
                key={device.id}
                device={device}
                onDeviceSelect={onDeviceSelect}
                hasPendingUpdate={!!pendingUpdates[device.id]}
                failedUpdate={failedUpdates[device.id]}
                onCancelUpdate={handleCancelUpdate}
                isFirst={index === 0}
                isCanceling={selectedDeviceId === device.id}
                onSync={handleSync}
              />
            ))}
            {sortedDevices.length === 0 && (
              <ListItem>
                <ListItemText 
                  primary={
                    <Typography 
                      variant="body1" 
                      color="text.secondary"
                      sx={{ textAlign: 'center' }}
                    >
                      Nenhum dispositivo encontrado
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
        </SortableContext>
      </DndContext>

      {/* Snackbar para feedback */}
      <Snackbar
        open={cancelStatus.show}
        autoHideDuration={5000}
        onClose={() => setCancelStatus({ ...cancelStatus, show: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={cancelStatus.success ? 'success' : 'error'} 
          sx={{ width: '100%' }}
        >
          {cancelStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeviceList;